import { Plus, X, Check, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROMPT_LEVELS, pct } from "@/lib/soap";
import { Card } from "@/components/SessionInfoCard";

const num = (v) => Math.max(0, Math.min(999, parseInt(v, 10) || 0));

function TargetRow({ t, i, update, remove }) {
  const p = pct(t.correct, t.total);
  const setCorrect = (v) => { const c = num(v); update({ correct: c, total: Math.max(t.total, c) }); };
  const setTotal = (v) => { const n = num(v); update({ total: n, correct: Math.min(t.correct, n) }); };
  return (
    <div className="rounded-lg border p-3 space-y-3" data-testid={`target-row-${i}`}>
      <div className="flex gap-2">
        <Input value={t.name} onChange={(e) => update({ name: e.target.value })} placeholder="Target name" className="h-9" data-testid={`target-name-input-${i}`} />
        <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={remove} data-testid={`target-remove-${i}`}><X className="h-4 w-4" /></Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => update({ correct: t.correct + 1, total: t.total + 1 })} data-testid={`target-trial-increment-button-${i}`}>
          <Check className="h-3.5 w-3.5 mr-1" /> Correct
        </Button>
        <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-700 hover:bg-red-50" onClick={() => update({ total: t.total + 1 })} data-testid={`target-trial-incorrect-button-${i}`}>
          <X className="h-3.5 w-3.5 mr-1" /> Incorrect
        </Button>
        <Button size="sm" variant="ghost" className="h-8 px-2" disabled={!t.total} onClick={() => update({ total: 0, correct: 0 })} data-testid={`target-reset-${i}`}>
          <Minus className="h-3.5 w-3.5" />
        </Button>
        <div className="flex items-center gap-1.5 ml-auto text-sm" data-testid={`target-score-${i}`}>
          <Input type="number" inputMode="numeric" min={0} value={t.correct} onFocus={(e) => e.target.select()} onChange={(e) => setCorrect(e.target.value)}
            className="h-8 w-14 px-1.5 text-center font-mono" aria-label="Correct trials" data-testid={`target-correct-input-${i}`} />
          <span className="text-slate-400">of</span>
          <Input type="number" inputMode="numeric" min={0} value={t.total} onFocus={(e) => e.target.select()} onChange={(e) => setTotal(e.target.value)}
            className="h-8 w-14 px-1.5 text-center font-mono" aria-label="Total trials" data-testid={`target-total-input-${i}`} />
          <span className={`font-mono w-11 text-right ${p >= 80 ? "text-emerald-600" : "text-amber-600"}`} data-testid={`target-percent-${i}`}>{p}%</span>
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${p}%` }} />
      </div>
      <Select value={t.prompt_level || "none"} onValueChange={(v) => update({ prompt_level: v === "none" ? "" : v })}>
        <SelectTrigger className="h-9 text-sm" data-testid={`target-prompt-select-${i}`}><SelectValue placeholder="Prompt level" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Prompt level…</SelectItem>
          {PROMPT_LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function TargetsCard({ targets, onChange }) {
  const update = (i, patch) => onChange(targets.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const add = () => onChange([...targets, { goal_id: "", name: "", correct: 0, total: 0, prompt_level: "", notes: "" }]);
  return (
    <Card title="Skill targets · trial data" action={<Button size="sm" variant="ghost" onClick={add} data-testid="add-target-button"><Plus className="h-4 w-4 mr-1" />Add</Button>}>
      {targets.length === 0 ? (
        <p className="text-sm text-slate-500">Pick a client to load their goals, or add a target.</p>
      ) : (
        <div className="space-y-3">
          {targets.map((t, i) => <TargetRow key={i} t={t} i={i} update={(p) => update(i, p)} remove={() => onChange(targets.filter((_, j) => j !== i))} />)}
        </div>
      )}
    </Card>
  );
}
